import React, { FC, useRef, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { TaskList } from 'components/molecules/tasks/task-list';
import { MinifiedTaskModal } from 'components/organisms/templates/task-modal';
import { DocumentResourceType } from 'data/documents';
import { Task } from 'data/tasks';
import { selectAllDocumentResourceSections } from 'data/templates/documents';
import { useTemplateMentionsData } from 'data/templates/hooks';
import { ProjectTemplateTask } from 'data/templates/projects';

const Wrapper = styled.div`
  margin-bottom: var(--layout-03);
`;

export const CustomTaskSection: FC<{
  docId: number;
  readOnly?: boolean;
  createResourceSection(
    resourceSectionId: number,
    resourceSectionName: string,
    resourceType: DocumentResourceType,
  ): void;
  handleAssigneeChange(
    taskId: number,
    selectedAssignee: any,
    resourceSectionId: number,
  ): void;
  handleNewTaskAdded({ taskName, resourceSectionId }): Promise<void>;
  onDeleteTask(task: ProjectTemplateTask, resourceSectionId: number): void;
}> = ({
  docId,
  readOnly,
  createResourceSection,
  handleAssigneeChange,
  handleNewTaskAdded,
  onDeleteTask,
}) => {
  const { members } = useTemplateMentionsData();
  const resourceSections = useSelector(
    selectAllDocumentResourceSections(docId),
  );
  const [activeResourceSectionId, setActiveResourceSectionId] =
    useState<number | null>(null);
  const [activeTask, setActiveTask] =
    useState<ProjectTemplateTask | null>(null);
  const [isModalVisible, toggleModalVisibility] = useState(false);
  const taskListRef = useRef<HTMLElement | null>(null);

  const taskSections = useMemo(
    () =>
      resourceSections.filter(
        (resourceSection) =>
          resourceSection.resourceType === DocumentResourceType.Custom,
      ),
    [resourceSections],
  );

  const onItemClick = (resourceSection, taskId) => {
    if (readOnly) {
      return;
    }
    setActiveResourceSectionId(resourceSection.resourceSectionId);
    setActiveTask(
      ((resourceSection.resourceObjects || []).find(
        (task) => task.taskId === taskId,
      ) || null) as ProjectTemplateTask,
    );
    toggleModalVisibility(true);
  };

  const taskFromStore = useMemo(
    () =>
      ((taskSections || [])
        .flatMap((section) => section.resourceObjects ?? [])
        .find((task) => task.taskId === activeTask?.taskId) ??
        null) as unknown as ProjectTemplateTask,
    [activeTask, taskSections],
  );

  return (
    <>
      {taskSections.map((resourceSection) => (
        <Wrapper key={resourceSection.resourceSectionId}>
          <h6>{resourceSection.resourceSectionName}</h6>
          <TaskList
            assignees={members}
            canAddDate={false}
            disableStatusDropdown
            readOnly={readOnly}
            size="small"
            onItemClick={([, , taskId]) => onItemClick(resourceSection, taskId)}
            refTo={taskListRef}
            data={resourceSection.resourceObjects as unknown[] as Task[]}
            handleStatusChange={() => {}}
            handleAssigneeChange={(taskId, assignee) =>
              handleAssigneeChange(
                taskId,
                assignee,
                resourceSection.resourceSectionId,
              )
            }
            handleDueDateChange={() => {}}
            handleDelete={(record) =>
              onDeleteTask(
                record as unknown as ProjectTemplateTask,
                resourceSection.resourceSectionId,
              )
            }
            handleNewTaskAdded={(taskName) =>
              handleNewTaskAdded({
                taskName,
                resourceSectionId: resourceSection.resourceSectionId,
              })
            }
            onEnableTaskAddRow={() =>
              createResourceSection(
                resourceSection.resourceSectionId,
                'Untitled tasks',
                resourceSection.resourceType,
              )
            }
          />
          {activeResourceSectionId && activeTask && (
            <MinifiedTaskModal
              docId={docId}
              resourceSectionId={activeResourceSectionId}
              task={taskFromStore}
              visible={isModalVisible}
              onClose={() => toggleModalVisibility(false)}
            />
          )}
        </Wrapper>
      ))}
    </>
  );
};
